import { Link } from "wouter";
import { Icon } from "./Icon";
import { IconButton } from "./IconButton";
import { UnitButton } from "./UnitButton";
import type { SpeedUnit } from "../lib/types";
import styles from "./Navbar.module.css";

interface NavbarBaseProps {
  unit: SpeedUnit;
  onCycleUnit: () => void;
  onOpenSearch?: () => void;
}

/**
 * Top navigation bar. Four variants:
 *
 * - `region` (regions overview / home) — "Bigwind.Sweden" wordmark on
 *   the left, search icon button on the right. No back button, no unit
 *   button.
 * - `spots`  (spots within a region) — back button on the left, region
 *   name centred (font-size scales by breakpoint to fit long names),
 *   search + unit buttons on the right.
 * - `detail` (single spot) — back button on the left, unit button on
 *   the right. No title, no search.
 * - `back-only` (about / static pages) — just a back button. No unit,
 *   no search, no title. For pages where wind units aren't relevant
 *   (e.g. /how-it-works).
 *
 * Layout-agnostic — the calling page wraps it in a sticky / content-
 * column shell as needed.
 */
type NavbarProps =
  | (NavbarBaseProps & { variant: "region"; onBack?: () => void })
  | (NavbarBaseProps & {
      variant: "spots";
      onBack: () => void;
      title: string;
    })
  | (NavbarBaseProps & {
      variant: "detail";
      onBack: () => void;
      /** When present, renders a body-styled spot name centered between
       *  the back button and the unit button. Used by the detail page
       *  when the user has scrolled past the spot header. */
      title?: string;
      /** Optional second line under the title (e.g. "Today · 12.00").
       *  Only rendered when `title` is also present. */
      subtitle?: string;
    })
  | { variant: "back-only"; onBack: () => void };

export function Navbar(props: NavbarProps) {
  if (props.variant === "region") {
    return (
      <nav className={`${styles.navbar} ${styles.navbarRegion}`}>
        {/* Left slot: back button when provided, otherwise a spacer that
            mirrors the right-side search button so the wordmark stays
            optically centred. */}
        {props.onBack ? (
          <IconButton
            variant="ghost"
            onClick={props.onBack}
            aria-label="Back"
          >
            <Icon name="arrow-left" />
          </IconButton>
        ) : (
          props.onOpenSearch && (
            <div className={styles.actionsSpacer} aria-hidden />
          )
        )}
        <Link href="/" className={styles.wordmarkLink}>
          <p className={styles.wordmark}>Bigwind.Sweden</p>
        </Link>
        {props.onOpenSearch && (
          <div className={styles.actions}>
            <SearchButton onClick={props.onOpenSearch} />
          </div>
        )}
      </nav>
    );
  }

  if (props.variant === "spots") {
    return (
      <nav className={styles.navbar}>
        <IconButton
          variant="ghost"
          onClick={props.onBack}
          aria-label="Back to regions"
        >
          <Icon name="arrow-left" />
        </IconButton>
        <h1 className={styles.title}>{props.title}</h1>
        <div className={styles.actions}>
          {props.onOpenSearch && <SearchButton onClick={props.onOpenSearch} />}
          <UnitButton unit={props.unit} onClick={props.onCycleUnit} />
        </div>
      </nav>
    );
  }

  if (props.variant === "back-only") {
    return (
      <nav className={styles.navbar}>
        <IconButton
          variant="ghost"
          onClick={props.onBack}
          aria-label="Back"
        >
          <Icon name="arrow-left" />
        </IconButton>
      </nav>
    );
  }

  // variant === "detail"
  return (
    <nav className={styles.navbar}>
      <IconButton
        variant="ghost"
        onClick={props.onBack}
        aria-label="Back"
      >
        <Icon name="arrow-left" />
      </IconButton>
      {props.title && (
        <div className={styles.titleStack}>
          <p className={styles.titleSubtle}>{props.title}</p>
          {props.subtitle && (
            <p className={styles.subtitle}>{props.subtitle}</p>
          )}
        </div>
      )}
      <UnitButton unit={props.unit} onClick={props.onCycleUnit} />
    </nav>
  );
}

interface SearchButtonProps {
  onClick: () => void;
}

/**
 * Search button — outline IconButton bumped from button-sm (default)
 * to button-md per the new navbar spec. Uses inline class merge since
 * we don't have a "size" prop on IconButton yet.
 */
function SearchButton({ onClick }: SearchButtonProps) {
  return (
    <button
      type="button"
      className={styles.searchBtn}
      onClick={onClick}
      aria-label="Search"
    >
      <Icon name="search" />
    </button>
  );
}
